import { supabase } from '@/integrations/supabase/client';
import { FiscalYear, CreateFiscalYearRequest, FiscalYearsResponse, UpdateFiscalYearRequest } from '@/types/fiscalYear';
import { toast } from 'sonner';
import { translateOutsideReact } from '@/i18n/translateOutsideReact';
import { handleError } from '@/utils/handleError';

export class FiscalYearApiClient {
  /**
   * Get all fiscal years for the current company
   * @returns Fiscal years response
   */
  static async getFiscalYears(): Promise<FiscalYearsResponse> {
    try {
      const { data, error } = await supabase.functions.invoke('fiscal-year-api', {
        body: { action: 'getFiscalYears' },
      });

      if (error) throw error;

      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchFiscalYears'), 'getFiscalYears');
      throw error;
    }
  }

  /**
   * Create a new fiscal year
   * @param request Fiscal year creation request
   * @returns Created fiscal year
   */
  static async createFiscalYear(request: CreateFiscalYearRequest): Promise<FiscalYear> {
    try {
      const { data, error } = await supabase.functions.invoke('fiscal-year-api', {
        body: {
          action: 'createFiscalYear',
          ...request
        },
      });

      if (error) throw error;

      toast.success(translateOutsideReact('consultantToasts.api.fiscalYearCreated'));
      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToCreateFiscalYear'), 'createFiscalYear');
      throw error;
    }
  }

  /**
   * Correct an already-open fiscal year's start/end dates (broken-fiscal-year.F2).
   * @param request The fiscal year id, its new dates, and the `updatedAt` last loaded
   * (optimistic-concurrency token — broken-fiscal-year.EC6)
   * @returns The updated fiscal year
   */
  static async updateFiscalYear(request: UpdateFiscalYearRequest): Promise<FiscalYear> {
    try {
      const { data, error } = await supabase.functions.invoke('fiscal-year-api', {
        body: {
          action: 'updateFiscalYear',
          ...request,
        },
      });

      if (error) throw error;

      toast.success(translateOutsideReact('consultantToasts.api.fiscalYearUpdated'));
      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToUpdateFiscalYear'), 'updateFiscalYear');
      throw error;
    }
  }

  /**
   * Close a fiscal year
   * @param fiscalYearId ID of the fiscal year to close
   * @returns Updated fiscal year
   */
  static async closeFiscalYear(fiscalYearId: string): Promise<FiscalYear> {
    try {
      const { data, error } = await supabase.functions.invoke('fiscal-year-api', {
        body: {
          action: 'closeFiscalYear',
          fiscalYearId
        },
      });

      if (error) throw error;

      toast.success(translateOutsideReact('consultantToasts.api.fiscalYearClosed'));
      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToCloseFiscalYear'), 'closeFiscalYear');
      throw error;
    }
  }
}
